import { useContext, useState } from "react";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import {
  ErrorDialogContext,
  errorHandledSync,
  errorHandledAsync,
} from "@/shared/context/error-context";
import { GetSearchConditionsService } from "@/features/accounts/shared/services/get-search-conditions-service";
import { RegisterSearchConditionsService } from "@/features/accounts/shared/services/register-search-conditions-service";
import { DeleteSearchConditionsService } from "@/features/accounts/shared/services/delete-search-conditions-service";
import { UpdateSearchConditionsService } from "@/features/accounts/shared/services/update-search-conditions-service";
import { MoneyLinkError, MoneyLinkException, ErrorLevel } from "@/shared/utils/custom-exception";

export const useConditionalSearch = () => {
  const {
    searchTradeType,
    setSearchTradeType,
    setCheckDeposit,
    setCheckWithdrawal,
    setCheckNoSpec,
    searchStartDate,
    setSearchStartDate,
    searchEndDate,
    setSearchEndDate,
    searchMinAmount,
    setSearchMinAmount,
    searchMaxAmount,
    setSearchMaxAmount,
    searchMinBalance,
    setSearchMinBalance,
    searchMaxBalance,
    setSearchMaxBalance,
    searchSummaryKeyword,
    setSearchSummaryKeyword,
    searchMemoKeyword,
    setSearchMemoKeyword,
  } = useTradesContext();
  const [check, setCheck] = useState(false);
  const [conditionName, setConditionName] = useState("");
  const [isActiveSearchButton, setIsActiveSearchButton] = useState(true);
  const getSearchConditionsService = new GetSearchConditionsService();
  const registerSearchConditionsService = new RegisterSearchConditionsService();
  const updateSearchConditionsService = new UpdateSearchConditionsService();
  const deleteSearchConditionsService = new DeleteSearchConditionsService();
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const getSearchConditions = async () => {
    try {
      const res = await getSearchConditionsService.getSearchConditions({});
      return res;
    } catch (error) {
      console.log("getSearchConditions", error);
      throw error;
      // return [];
    } finally {
      //
    }
  };

  const registerSearchConditions = async () => {
    try {
      const summaryForApi = searchSummaryKeyword.slice(0, 500);
      const memoForApi = searchMemoKeyword.slice(0, 500);
      await registerSearchConditionsService.registerSearchConditions({
        condition_name: conditionName,
        trade_type: searchTradeType,
        ...(searchStartDate !== "" && { date_start: searchStartDate }),
        ...(searchEndDate !== "" && { date_end: searchEndDate }),
        ...(summaryForApi !== "" && { transact_match: summaryForApi }),
        ...(searchMinAmount !== undefined && { amount_low: Number(searchMinAmount) }),
        ...(searchMaxAmount !== undefined && { amount_high: Number(searchMaxAmount) }),
        ...(searchMinBalance !== undefined && { balance_low: Number(searchMinBalance) }),
        ...(searchMaxBalance !== undefined && { balance_high: Number(searchMaxBalance) }),
        ...(memoForApi !== "" && { memo_match: memoForApi }),
      });
    } catch (error) {
      console.log("registerSearchConditions", error);
      // バリデーションエラー(webmoneylink_parameter_error)時は専用メッセージに差し替える
      if (
        error instanceof MoneyLinkException &&
        error.error.props.reason === "webmoneylink_parameter_error"
      ) {
        throw new MoneyLinkException({
          error: new MoneyLinkError({
            level: ErrorLevel.Warn,
            code: error.error.props.code,
            reason: error.error.props.reason,
            messageTitle: undefined,
            messageBody:
              "使用できない文字が含まれているか、文字数が上限を超えています。\n入力内容をご確認ください。",
          }),
        });
      }
      throw error;
    } finally {
      //
    }
  };

  const updateSearchConditions = async (conditionId: string, conditionName: string) => {
    try {
      const summaryForApi = searchSummaryKeyword.slice(0, 500);
      const memoForApi = searchMemoKeyword.slice(0, 500);
      await updateSearchConditionsService.updateSearchConditions({
        condition_id: conditionId,
        condition_name: conditionName,
        trade_type: searchTradeType,
        ...(searchStartDate !== "" && { date_start: searchStartDate }),
        ...(searchEndDate !== "" && { date_end: searchEndDate }),
        ...(summaryForApi !== "" && { transact_match: summaryForApi }),
        ...(searchMinAmount !== undefined && { amount_low: Number(searchMinAmount) }),
        ...(searchMaxAmount !== undefined && { amount_high: Number(searchMaxAmount) }),
        ...(searchMinBalance !== undefined && { balance_low: Number(searchMinBalance) }),
        ...(searchMaxBalance !== undefined && { balance_high: Number(searchMaxBalance) }),
        ...(memoForApi !== "" && { memo_match: memoForApi }),
      });
    } catch (error) {
      console.log("updateSearchConditions", error);
      if (
        error instanceof MoneyLinkException &&
        error.error.props.reason === "webmoneylink_parameter_error"
      ) {
        throw new MoneyLinkException({
          error: new MoneyLinkError({
            level: ErrorLevel.Warn,
            code: error.error.props.code,
            reason: error.error.props.reason,
            messageTitle: undefined,
            messageBody:
              "使用できない文字が含まれているか、文字数が上限を超えています。\n入力内容をご確認ください。",
          }),
        });
      }
      throw error;
    } finally {
      //
    }
  };

  const deleteSearchConditions = async (conditionId: string) => {
    try {
      await deleteSearchConditionsService.deleteSearchConditions({
        condition_id: conditionId,
      });
    } catch (error) {
      console.log("deleteSearchConditions", error);
      throw error;
    } finally {
      //
    }
  };

  const changeSearchButtonState = () => {
    if (check) {
      if (conditionName.trim() === "") {
        setIsActiveSearchButton(false);
      } else {
        setIsActiveSearchButton(true);
      }
    } else {
      setIsActiveSearchButton(true);
    }
  };

  // チェックボックス押下時の処理
  const onTapCheck = () => {
    if (check) {
      setCheck(false);
    } else {
      setCheck(true);
    }
  };

  const onChangeTitleForm = (value: string) => {
    setConditionName(value.trim());
  };

  const onSelectTradeType = (value: string) => {
    if (value === "in") {
      setCheckDeposit(true);
      setCheckWithdrawal(false);
      setCheckNoSpec(false);
      setSearchTradeType(1); // 1:入金
    } else if (value === "out") {
      setCheckDeposit(false);
      setCheckWithdrawal(true);
      setCheckNoSpec(false);
      setSearchTradeType(2); // 2:出金
    } else {
      setCheckDeposit(false);
      setCheckWithdrawal(false);
      setCheckNoSpec(true);
      setSearchTradeType(0); // 0:全て
    }
  };

  const onChangeSummaryForm = (value: string) => {
    setSearchSummaryKeyword(value.trim());
  };

  const onChangeStartDateForm = (value: string) => {
    // YYYY-MM-DD => YYYYMMDD に変換してセット
    setSearchStartDate(value.replace(/-/g, ""));
  };
  
  const onChangeEndDateForm = (value: string) => {
    // YYYY-MM-DD => YYYYMMDD に変換してセット
    setSearchEndDate(value.replace(/-/g, ""));
  };

  const onChangeMinAmountForm = (value: string) => {
    let num;
    if (value === "") {
      num = undefined;
    } else {
      num = Number(value);
    }
    setSearchMinAmount(num);
  };

  const onChangeMaxAmountForm = (value: string) => {
    let num;
    if (value === "") {
      num = undefined;
    } else {
      num = Number(value);
    }
    setSearchMaxAmount(num);
  };

  const onChangeMinBalanceForm = (value: string) => {
    let num;
    if (value === "") {
      num = undefined;
    } else {
      num = Number(value);
    }
    setSearchMinBalance(num);
  };

  const onChangeMaxBalanceForm = (value: string) => {
    let num;
    if (value === "") {
      num = undefined;
    } else {
      num = Number(value);
    }
    setSearchMaxBalance(num);
  };

  const onChangeMemoForm = (value: string) => {
    setSearchMemoKeyword(value.trim());
  };

  return {
    check,
    setCheck,
    conditionName,
    setConditionName,
    isActiveSearchButton,
    setIsActiveSearchButton,
    getSearchConditions: errorHandledAsync(getSearchConditions, setError),
    registerSearchConditions: errorHandledAsync(registerSearchConditions, setError),
    updateSearchConditions: errorHandledAsync(updateSearchConditions, setError),
    deleteSearchConditions: errorHandledAsync(deleteSearchConditions, setError),
    changeSearchButtonState: errorHandledSync(changeSearchButtonState, setError),
    onTapCheck: errorHandledSync(onTapCheck, setError),
    onChangeTitleForm: errorHandledSync(onChangeTitleForm, setError),
    onSelectTradeType: errorHandledSync(onSelectTradeType, setError),
    onChangeSummaryForm: errorHandledSync(onChangeSummaryForm, setError),
    onChangeStartDateForm: errorHandledSync(onChangeStartDateForm, setError),
    onChangeEndDateForm: errorHandledSync(onChangeEndDateForm, setError),
    onChangeMinAmountForm: errorHandledSync(onChangeMinAmountForm, setError),
    onChangeMaxAmountForm: errorHandledSync(onChangeMaxAmountForm, setError),
    onChangeMinBalanceForm: errorHandledSync(onChangeMinBalanceForm, setError),
    onChangeMaxBalanceForm: errorHandledSync(onChangeMaxBalanceForm, setError),
    onChangeMemoForm: errorHandledSync(onChangeMemoForm, setError),
  };
};